import { useState } from 'react';
import { useFrontendTool } from '@copilotkit/react-core';
import { Card, Pagination, Skeleton, Stack, Text, Title } from '@mantine/core';

import { MultiStreamWidget } from 'types/dashboard_widget';

import { useStreams } from 'streams/hooks/useStreams';
import { useMqttStreamsState } from 'streams/hooks/useMqttStreamsState';

import { VideoSourceSnapshot } from 'components/VideoSourceSnapshot/VideoSourceSnapshot';

import { AddToDashboardMenu } from './AddToDashboardMenu';

const PAGE_SIZE = 6;

type ShowStreamsArgs = {
  streamIds?: string;
  cameraIds?: string;
  gatewayIds?: string;
  deploymentIds?: string;
  autoPlay?: boolean;
  limit?: number;
};

type ShowStreamsRenderProps = {
  status: 'complete' | 'executing' | 'inProgress';
  args: ShowStreamsArgs;
};

function safeJsonParse<T>(json: string | T | undefined, fallback: T): T {
  if (!json) return fallback;
  if (typeof json !== 'string') return json;
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

function StreamsRenderer({ status, args }: ShowStreamsRenderProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const {
    streamIds: streamIdsJson,
    cameraIds: cameraIdsJson,
    gatewayIds: gatewayIdsJson,
    deploymentIds: deploymentIdsJson,
    autoPlay = false,
    limit = PAGE_SIZE,
  } = args;

  const streamIds: string[] | undefined = safeJsonParse(streamIdsJson, undefined);
  const cameraIds: string[] | undefined = safeJsonParse(cameraIdsJson, undefined);
  const gatewayIds: string[] | undefined = safeJsonParse(gatewayIdsJson, undefined);
  const deploymentIds: string[] | undefined = safeJsonParse(deploymentIdsJson, undefined);

  const { data: streams, isLoading } = useStreams({
    params: {
      stream_ids: streamIds,
      camera_ids: cameraIds,
      gateway_ids: gatewayIds,
      deployment_ids: deploymentIds,
      page: currentPage,
      limit,
    },
    enabled: status === 'complete',
  });

  useMqttStreamsState(streams?.data ? streams.data.map(({ id }) => id) : []);

  const createWidget = () => {
    const defaultLayout = { x: 0, y: 0, w: 4, h: 3 };
    
    return new MultiStreamWidget(
      crypto.randomUUID(),
      'Streams',
      {
        stream_ids: streamIds,
        camera_ids: cameraIds,
        gateway_ids: gatewayIds,
        deployment_ids: deploymentIds,
      },
      autoPlay,
      { ...defaultLayout, w: 6, h: 4 }
    );
  };

  if (status === 'inProgress' || status === 'executing' || isLoading) {
    return (
      <Card withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text c="dimmed" size="sm">
            Loading streams...
          </Text>
          <div className="@container">
            <div className="grid grid-cols-1 gap-2 @md:grid-cols-3">
              {Array.from({ length: Math.min(limit, 6) }).map((_, i) => (
                <Skeleton key={i} height={120} radius="md" />
              ))}
            </div>
          </div>
        </Stack>
      </Card>
    );
  }

  const hasMatchingStreams = streams?.data && streams.data.length > 0;
  const hasPagination = streams && streams.total_pages > 1;

  if (!hasMatchingStreams) {
    return (
      <Card withBorder p="md" radius="md">
        <Text size="sm" c="dimmed">
          No streams found matching the filter criteria.
        </Text>
      </Card>
    );
  }

  return (
    <Card withBorder p="md" radius="md" className="relative">
      <Stack gap="sm">
        <Title order={5}>
          Streams ({streams.total_elements})
        </Title>
        <div className="@container">
          <div className="grid grid-cols-1 gap-2 @md:grid-cols-3">
            {streams.data.map((stream) => {
              if (!stream.id) return null;

              return (
                <div
                  key={stream.id}
                  className="overflow-hidden rounded-md border border-neutral-200 dark:border-neutral-700"
                >
                  <VideoSourceSnapshot
                    className="aspect-video w-full"
                    source={stream}
                    autoRefresh
                  />
                </div>
              );
            })}
          </div>
        </div>

        {hasPagination && (
          <Pagination
            total={streams.total_pages}
            value={currentPage}
            onChange={setCurrentPage}
            size="sm"
          />
        )}
      </Stack>
      <div className="absolute bottom-2 right-2">
        <AddToDashboardMenu createWidget={createWidget} />
      </div>
    </Card>
  );
}

export function useShowStreamsTool() {
  useFrontendTool(
    {
      name: 'showStreams',
      description:
        'Display video streams in a grid view. Shows stream snapshots or live video. Can filter by stream IDs, camera IDs, gateway IDs, or deployment IDs. Users can click on streams to search their recordings.',
      parameters: [
        {
          name: 'streamIds',
          type: 'string',
          description:
            'JSON array of specific stream IDs to display. If not provided, shows all streams.',
          required: false,
        },
        {
          name: 'cameraIds',
          type: 'string',
          description:
            'JSON array of camera IDs to filter streams by.',
          required: false,
        },
        {
          name: 'gatewayIds',
          type: 'string',
          description:
            'JSON array of gateway IDs to filter streams by.',
          required: false,
        },
        {
          name: 'deploymentIds',
          type: 'string',
          description:
            'JSON array of deployment IDs to filter streams by.',
          required: false,
        },
        {
          name: 'autoPlay',
          type: 'boolean',
          description:
            'If true, shows live video streams. If false (default), shows stream snapshots.',
          required: false,
        },
        {
          name: 'limit',
          type: 'number',
          description: 'Maximum number of streams to display per page. Defaults to 6.',
          required: false,
        },
      ],
      handler: async (args: ShowStreamsArgs) => {
        return { ...args, message: 'Streams displayed to user' };
      },
      render: ({ status, args }) => (
        <StreamsRenderer status={status} args={args as ShowStreamsArgs} />
      ),
    },
    []
  );
}
